import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Code2, ExternalLink } from "lucide-react";
import { SiGithub } from "react-icons/si";
// Data extracted outside the component to prevent re-renders
const FILTERS = [
  { id: "all", label: "All Projects" },
  { id: "frontend", label: "Frontend" },
  { id: "backend", label: "Backend" },
  { id: "fullstack", label: "Full Stack" },
];

const PROJECTS = [
  {
    id: 1,
    title: "Ethio Language Learning Platform",
    description: "A web platform for learning Ethiopian languages interactively. Features lessons, quizzes, and progress tracking.",
    image: "/images/ethio-language-learning-platform.png",
    technologies: ["React", "Vite"],
    github: "https://github.com/gebrie-dev/Ethio_language_Learning",
    live: "https://ethiolanguage.netlify.app",
    category: "frontend",
  },
  {
    id: 2,
    title: "Abraham Translation Service",
    description: "Dynamic translation service for clients that enables customers to securely send their documents for translation.",
    image: "/images/culer-hub.png",
    technologies: ["Next.js", "TypeScript", "Tailwind CSS"],
    github: "https://github.com/gebrie-dev/",
    live: "https://abrahamtranslation.netlify.app/",
    category: "fullstack",
  },
  {
    id: 3,
    title: "ChannelX",
    description: "A modern frontend application heavily utilizing API integration for real-time data handling.",
    image: "/images/channelx.png",
    technologies: ["React", "API Integration"],
    github: "https://github.com/gebrie-dev/channelx.git",
    live: "https://channelx-4lh2.vercel.app/",
    category: "frontend",
  },
  {
    id: 4,
    title: "EthioMarket",
    description: "A frontend e-commerce platform for Ethiopian products. Features inventory management, secure payments, and user accounts.",
    image: "/images/EthioMarket.png",
    technologies: ["Next.js", "TypeScript", "Tailwind CSS"],
    github: "https://github.com/gebrie-dev/Ethiomarket",
    live: "https://ethiomarket.netlify.app/",
    category: "frontend",
  },
  {
    id: 5,
    title: "Gebre Books",
    description: "A RESTful API designed to simplify the management of book collections with robust features like user authentication and CRUD operations.",
    image: "/images/books.png",
    technologies: ["Node.js", "MongoDB", "Express", "Swagger UI"],
    github: "https://gebre-books.onrender.com/api-docs/",
    live: "https://ethiomarket-demo.netlify.app/",
    category: "backend",
  },
  {
    id: 6,
    title: "Local Sustainability & Recycling Exchange",
    description: "A full-stack marketplace aimed at connecting communities for local sustainability and recycling exchanges.",
    image: "/images/localsus.png",
    technologies: ["Next.js", "TypeScript", "Tailwind CSS"],
    github: "https://github.com/yabibal-Hu/-Local-Sustainability-Recycling-Exchange/tree/backend",
    live: "https://local-sustainability-recycling-exchange.vercel.app/",
    category: "fullstack",
  },
];

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  const filteredProjects = activeFilter === "all"
    ? PROJECTS
    : PROJECTS.filter((project) => project.category === activeFilter);

  return (
    <section id="projects" className="py-24 bg-[#FAFAFA] dark:bg-[#09090B] px-4 sm:px-6 relative overflow-hidden">
      
      {/* Subtle background glow effect */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-[400px] bg-emerald-500/5 dark:bg-emerald-500/10 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 mb-6">
            <Code2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-zinc-900 dark:text-white mb-6">
            Engineering <span className="text-zinc-400 dark:text-zinc-500">Showcase</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-zinc-600 dark:text-zinc-400 leading-relaxed">
            A selection of recent platforms and applications I've architected, focusing on scalable infrastructure, modern frontend frameworks, and clean UI/UX.
          </p>

          {/* Premium Segmented Control Filtering */}
          <div className="flex flex-wrap justify-center gap-2 mt-12 p-1.5 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 rounded-full max-w-fit mx-auto">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`relative px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                  activeFilter === filter.id
                    ? "text-white dark:text-zinc-900"
                    : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                }`}
              >
                {activeFilter === filter.id && (
                  <motion.div
                    layoutId="active-pill"
                    className="absolute inset-0 bg-zinc-900 dark:bg-zinc-100 rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{filter.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                key={project.id}
                className="group flex flex-col bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 rounded-3xl overflow-hidden hover:shadow-2xl hover:shadow-zinc-900/10 dark:hover:shadow-white/5 transition-all duration-500"
              >
                {/* Image Container with strict aspect ratio */}
                <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100 dark:bg-zinc-800 border-b border-zinc-200 dark:border-white/10">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/10 transition-colors duration-500" />
                </div>

                <div className="p-8 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-3 tracking-tight">
                    {project.title}
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6 line-clamp-3 leading-relaxed flex-grow">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 rounded-lg text-xs font-medium bg-zinc-100 text-zinc-600 dark:bg-white/5 dark:text-zinc-300 border border-zinc-200 dark:border-white/5"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-zinc-100 dark:border-white/10 mt-auto">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors"
                    >
<SiGithub className="w-4 h-4 mr-2" />
                      Source
                    </a>
                    <a
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white hover:scale-105 active:scale-95"
                    >
                      View Live
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;